import type { InvestorDashboardDto } from '../services/investor.service';

/**
 * THE single definition of the investor journey steps, their order, and when each counts
 * as done. Used by the stepper (highlighting) AND by post-completion navigation (where to
 * land after Final Submit), so the page the investor lands on is always the step the
 * stepper highlights. Done-rules mirror Laravel getProgressSteps: documents/onboarding tick
 * on APPROVED, not merely uploaded.
 */
export type JourneyStepKey = 'information' | 'documents' | 'onboarding' | 'verification' | 'physical' | 'account';

export interface JourneyStep {
  key: JourneyStepKey;
  label: string;
  icon: string;
  /** Investor route; SA proxy mode maps /investor/… → /service-agent/investors/{id}/… */
  path: string;
}

export const JOURNEY_STEPS: readonly JourneyStep[] = [
  { key: 'information', label: 'Information', icon: 'bi-person-lines-fill', path: '/investor/journey' },
  { key: 'documents', label: 'KYC Docs', icon: 'bi-file-earmark-lock-fill', path: '/investor/documents' },
  { key: 'onboarding', label: 'Onboarding', icon: 'bi-file-earmark-richtext-fill', path: '/investor/onboarding' },
  { key: 'verification', label: 'Verification', icon: 'bi-person-video', path: '/investor/verification' },
  { key: 'physical', label: 'Physical', icon: 'bi-send-check-fill', path: '/investor/physical-submission' },
  { key: 'account', label: 'Account', icon: 'bi-bank2', path: '/investor/account-details' },
];

export function isStepDone(key: JourneyStepKey, dashboardData: InvestorDashboardDto | null | undefined): boolean {
  if (!dashboardData) return false;
  const progress = dashboardData.progress;
  const accountSummary = dashboardData.accountSummary;
  switch (key) {
    case 'information':
      return progress?.sections?.personalInfo?.completed || false;
    case 'documents': {
      if (!accountSummary) return false;
      const required = accountSummary.kycDocumentsRequired;
      const approved = accountSummary.kycDocumentsApproved ?? accountSummary.kycDocumentsUploaded;
      // required > 0 guard: the backend sizes 'required' as max(requirement, uploaded), so an
      // untouched journey reads 0/0 — without the guard that would tick as done.
      return required > 0 && approved >= required;
    }
    case 'onboarding': {
      if (!accountSummary) return false;
      const req = accountSummary.onboardingDocumentsRequired;
      const approved = accountSummary.onboardingDocumentsApproved ?? accountSummary.onboardingDocumentsUploaded;
      return req > 0 && approved >= req;
    }
    case 'verification':
      return accountSummary?.verificationDone || false;
    case 'physical':
      return accountSummary?.physicalSubmissionDone || false;
    case 'account':
      return accountSummary?.accountOpeningStatus || false;
    default:
      return false;
  }
}

/** Laravel parity: In-person Verification stays locked until every onboarding document is approved. */
export function isStepLocked(key: JourneyStepKey, dashboardData: InvestorDashboardDto | null | undefined): boolean {
  return key === 'verification' && !isStepDone('verification', dashboardData) && !isStepDone('onboarding', dashboardData);
}

/** The first step that is not done — what the stepper highlights when no route matches. */
export function firstIncompleteStep(dashboardData: InvestorDashboardDto | null | undefined): JourneyStep {
  return JOURNEY_STEPS.find((s) => !isStepDone(s.key, dashboardData)) ?? JOURNEY_STEPS[0];
}

/**
 * Where to send the investor once a step has just been completed: the next incomplete step
 * AFTER the one just finished (never backwards, never a locked step). If everything after it
 * is done or locked, stay on the step just completed so the page shows its finished state.
 * The Information route carries the journey id (multi-journey scope); the other routes read
 * the active journey from session scope.
 */
export function nextStepPathAfter(
  completed: JourneyStepKey,
  dashboardData: InvestorDashboardDto | null | undefined,
  journeyId?: string | null,
): string {
  const idx = JOURNEY_STEPS.findIndex((s) => s.key === completed);
  const next = JOURNEY_STEPS.slice(idx + 1).find((s) => !isStepDone(s.key, dashboardData) && !isStepLocked(s.key, dashboardData));
  const target = next ?? JOURNEY_STEPS[idx];
  if (target.key === 'information') {
    return journeyId ? `/investor/journey/${encodeURIComponent(journeyId)}` : target.path;
  }
  return target.path;
}

/** Investor step path → its service-agent proxy equivalent (the journey page is /profile there). */
export function toServiceAgentPath(investorPath: string, saInvestorId: number): string {
  if (investorPath.startsWith('/investor/journey')) return `/service-agent/investors/${saInvestorId}/profile`;
  return investorPath.replace('/investor/', `/service-agent/investors/${saInvestorId}/`);
}
